import React, { useMemo, useRef, useState } from 'react'
import cx from 'classnames'
import ReactFocusLock, { FreeFocusInside } from 'react-focus-lock'
import { useFieldArray, useFormContext, useFormState } from 'react-hook-form'
import { useAppSelector } from 'reduxTyped'
import { BtnV2, l, Popover } from '@manychat/manyui'

import { FieldGroupId } from 'common/fields/entities/enums'
import { getAiLeadQualAvailableFields } from 'common/fields/linking/accountAvailableFields'
import FieldSearch from 'common/fields/linking/components/FieldSearch'
import { getField } from 'common/fields/linking/components/utils'
import { UserFieldType } from 'shared/api/common/types'

import { MAX_FIELDS_COUNT } from '../../../../lib/constants'
import { ConfigFormValues } from '../../../../lib/types'
import { DraggableField } from '../DraggableField/DraggableField'
import { SelectedField } from '../SelectedField/SelectedField'

import cm from './FieldsEditor.module.css'

const allowedTypes = [
  UserFieldType.TEXT,
  UserFieldType.NUMBER,
  UserFieldType.DATE,
  UserFieldType.DATETIME,
  UserFieldType.BOOL,
]

export const FieldsEditor = ({ isActive }: { isActive: boolean }) => {
  const { setValue } = useFormContext<ConfigFormValues>()

  const {
    append,
    remove,
    fields: selectedFields,
    move,
    update,
  } = useFieldArray<ConfigFormValues>({
    name: 'fields',
  })

  const { errors } = useFormState<ConfigFormValues>()

  const fields = useAppSelector(getAiLeadQualAvailableFields)

  const [isPickerOpen, setIsPickerOpen] = useState(false)

  const fieldIndexToReplace = useRef<number | null>(null)

  const anchorRef = useRef<HTMLDivElement | null>(null)

  const fieldsWithoutSelected = useMemo(() => {
    if (selectedFields.length === 0) {
      return fields
    }

    return fields.filter(
      (field) => !selectedFields.some((selectedField) => selectedField.fieldId === field.id),
    )
  }, [fields, selectedFields])

  const onFieldSelect = (fieldId: string) => {
    const field = getField(fields, fieldId)

    if (!field) {
      return
    }

    const fieldAlreadySelected = selectedFields.some(
      (selectedField) => selectedField.fieldId === fieldId,
    )

    if (fieldAlreadySelected) {
      return
    }

    if (MAX_FIELDS_COUNT - selectedFields.length === 1) {
      closePicker()
      return
    }

    if (field.id === 'phone') {
      setValue('useWaId', true)
    }

    if (fieldIndexToReplace.current === null) {
      append({ fieldId: field.id })
      return
    }

    if (selectedFields[fieldIndexToReplace.current].fieldId === 'phone') {
      setValue('useWaId', false)
    }

    update(fieldIndexToReplace.current, { fieldId: field.id })

    fieldIndexToReplace.current = null
    closePicker()
  }

  const openPicker = () => {
    setIsPickerOpen(true)
  }

  const closePicker = () => {
    fieldIndexToReplace.current = null
    setIsPickerOpen(false)
  }

  const openPickerForSelectedField = (index: number) => {
    if (!isActive) {
      return
    }

    fieldIndexToReplace.current = index
    openPicker()
  }

  return (
    <div>
      {isActive && selectedFields.length > 0 && selectedFields.length < MAX_FIELDS_COUNT && (
        <div className="d-flex justify-end m-b">
          <BtnV2 onClick={openPicker} leftIcon="Plus" variant="ghost-primary">
            {l.translate('Select info')}
          </BtnV2>
        </div>
      )}

      <div ref={anchorRef}>
        {selectedFields.length === 0 && (
          <div className={cm.emptyState}>
            <BtnV2
              onClick={openPicker}
              leftIcon="Plus"
              variant="ghost-primary"
              label={l.translate('Select info')}
            />
          </div>
        )}

        {selectedFields.length > 0 && (
          <ul
            className={cx(cm.selectedFieldsList, 'no-select')}
            aria-label={l.translate('Fields to collect')}
          >
            {selectedFields.map((field, index) => (
              <DraggableField moveGoal={move} key={field.id} index={index}>
                <SelectedField
                  onDelete={() => remove(index)}
                  key={field.id}
                  disabled={!isActive}
                  fieldId={field.fieldId}
                  index={index}
                  onFieldClick={openPickerForSelectedField}
                />
              </DraggableField>
            ))}
          </ul>
        )}

        {errors.fields?.message && (
          <span className="m-t-xs p-l-xs d-block text-sm text-danger">{errors.fields.message}</span>
        )}
      </div>

      <ReactFocusLock disabled={!isPickerOpen}>
        <FreeFocusInside>
          <Popover
            open={isPickerOpen}
            anchor={anchorRef.current}
            at="top"
            arrow
            onRequestClose={closePicker}
          >
            <FieldSearch
              fields={fieldsWithoutSelected}
              onSelect={onFieldSelect}
              allowNew={FieldGroupId.USER}
              allowedUserFieldTypes={allowedTypes}
            />
          </Popover>
        </FreeFocusInside>
      </ReactFocusLock>
    </div>
  )
}
